import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@finitless/shared/lib";
import { apiClient } from "@finitless/shared/lib";
import { useUserStore } from "@finitless/shared/stores";

/**
 * Hook to fetch user sessions
 */
const useSessions = () => {
  const { user } = useUserStore();
  const queryClient = useQueryClient();

  // Fetch sessions
  const {
    data: sessions,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.user.sessions(user?.id),
    queryFn: async () => {
      if (!user?.id) {
        throw new Error("User ID is required");
      }

      const response = await apiClient.get(`/users/${user.id}/sessions`);

      if (response.error) {
        const errorMessage = Array.isArray(response.result)
          ? response.result.join(", ")
          : response.result || "Failed to fetch sessions";
        throw new Error(errorMessage);
      }

      const sessions = response.result || [];

      // Filter only valid sessions and sort them
      const validSessions = sessions.filter(
        (session) => session.valid === true,
      );

      // Sort sessions: current session first, then by created_at (newest to oldest)
      return validSessions.sort((a, b) => {
        // If one is current, prioritize it
        if (a.current && !b.current) return -1;
        if (!a.current && b.current) return 1;

        // Otherwise sort by created_at (newest first)
        return new Date(b.created_at) - new Date(a.created_at);
      });
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
  });

  // Revoke single session mutation
  const revokeSessionMutation = useMutation({
    mutationKey: ["user", "revokeSession"],
    mutationFn: async (sessionId) => {
      if (!user?.id) {
        throw new Error("User ID is required");
      }

      const response = await apiClient.delete(
        `/users/${user.id}/sessions/${sessionId}`,
      );

      if (response.error) {
        const errorMessage = Array.isArray(response.result)
          ? response.result.join(", ")
          : response.result || "Failed to revoke session";
        throw new Error(errorMessage);
      }

      return response.result;
    },
    onSuccess: () => {
      // Invalidate and refetch sessions
      queryClient.invalidateQueries({
        queryKey: queryKeys.user.sessions(user?.id),
      });
    },
    onError: (error) => {
      console.error("Session revoke failed:", error);
    },
  });

  // Revoke all sessions mutation
  const revokeAllSessionsMutation = useMutation({
    mutationKey: ["user", "revokeAllSessions"],
    mutationFn: async () => {
      if (!user?.id) {
        throw new Error("User ID is required");
      }

      const response = await apiClient.delete(`/users/${user.id}/sessions/all`);

      if (response.error) {
        const errorMessage = Array.isArray(response.result)
          ? response.result.join(", ")
          : response.result || "Failed to revoke all sessions";
        throw new Error(errorMessage);
      }

      return response.result;
    },
    onSuccess: () => {
      // Invalidate and refetch sessions
      queryClient.invalidateQueries({
        queryKey: queryKeys.user.sessions(user?.id),
      });
    },
    onError: (error) => {
      console.error("Revoke all sessions failed:", error);
    },
  });

  return {
    sessions,
    isLoading,
    error,
    refetch,
    revokeSession: revokeSessionMutation.mutateAsync,
    revokeAllSessions: revokeAllSessionsMutation.mutateAsync,
    isRevokingSession: revokeSessionMutation.isPending,
    isRevokingAllSessions: revokeAllSessionsMutation.isPending,
  };
};

export { useSessions };
